import { Divider, Popover, Space, Typography } from 'antd';
import { AbstractTooltipProps } from 'antd/lib/tooltip';
import { CSSProperties, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Comment, commentType, commentTypeOptionsMap, status } from '../model/types';
import CustomDivider from './CustomDivider';
import MiniTag from './MiniTag';
import QuillGrispiRichTextEditor from './QuillGrispiRichTextEditor';
import { ConversationList } from './TicketPage/Conversations';
const { Text } = Typography;

interface PopoverTicketDetailProps {
    children?: ReactNode;
    ticketKey: string;
    subject: string;
    ticketStatus: status;
    lastTwoComments?: Comment[];
    description?: string;
    requester?: string;
    assignee?: string;
    descriptionType?: commentType;
    extraContent?: ReactNode;
    placement?: AbstractTooltipProps['placement'];
    style?: CSSProperties;
    isVisible?: boolean;
}

export default function PopoverTicketDetail({
    children,
    ticketKey,
    subject,
    lastTwoComments,
    description,
    requester,
    assignee,
    ticketStatus,
    descriptionType = 'public',
    extraContent,
    placement = 'bottomLeft',
    style,
    isVisible = true
}: PopoverTicketDetailProps) {
    const { t } = useTranslation('tickets');
    const { t: tForms } = useTranslation('forms');
    const [open, setOpen] = useState(false);
    useEffect(() => {
        !isVisible && setOpen(false);
    }, [isVisible]);

    return (
        <Popover
            overlayStyle={{
                ...style,
                zIndex: 99999
            }}
            placement={placement}
            title={
                <Space align="center">
                    <MiniTag status={ticketStatus} />
                    <Text strong style={{ fontSize: 15 }}>
                        {ticketKey}
                    </Text>
                </Space>
            }
            content={
                <Space direction="vertical" size="large" style={{ maxWidth: 400 }}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <Text strong>{`${tForms('Subject')}: ${subject}`}</Text>
                        {requester && <Text style={{ fontSize: 14 }}>{`${tForms('Requester')}: ${requester}`}</Text>}
                        {assignee && <Text style={{ fontSize: 14 }}>{`${tForms('Assignee')}: ${assignee}`}</Text>}
                        <CustomDivider zeroMargin />
                        {lastTwoComments && (
                            <ConversationList showAvatar={false} showAttachments={false} comments={lastTwoComments} />
                        )}

                        {description && (
                            <div style={{ padding: '0.5rem 0', maxHeight: 200, overflowY: 'auto' }}>
                                <QuillGrispiRichTextEditor
                                    key={description}
                                    readOnly
                                    internalView={descriptionType === commentTypeOptionsMap.internal}
                                    value={description}
                                />
                            </div>
                        )}
                    </Space>
                    {extraContent && (
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text type="secondary">{t('latestComments')}</Text>
                            <Divider style={{ marginTop: 0, marginBottom: 5 }} />
                            {extraContent}
                        </Space>
                    )}
                </Space>
            }
            open={open && isVisible}
            trigger={isVisible ? ['hover'] : []}
            onOpenChange={(isOpen) => setOpen(isOpen)}
        >
            {children}
        </Popover>
    );
}
